import { alpha, Box, Stack, Theme, Tooltip, Typography } from '@mui/material';
import Switch from '@mui/material/Switch/Switch';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { FormComponentBuilder } from 'components/common/form/layout';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { GetNotificationStatus, myNotificationAPI } from 'api/my-notification';
import useBundleTranslation from 'i18n';
import { useLocation } from 'react-router-dom';
import { lighten } from '@mui/system/colorManipulator';
import { useTheme } from '@mui/material/styles';
import { SystemDateTimeLabel } from 'components/common/system-date-time/SystemDateTimeLabel';

export interface MyNotificationInfoBuilderProps extends FormControlProps {
    showSwitch: boolean;
}

export default function MyNotificationInfo({
    controlProps,
}: FormElementControlPropsType<MyNotificationInfoBuilderProps>) {
    const { t } = useBundleTranslation(['app/notification/my-notification']);
    const location = useLocation();
    const appTheme = useTheme();

    const [initialState, setInitialState] = useState<boolean>(true);
    const [notificationStatus, setNotificationStatus] = useState(false);

    const [myNotificationStatus, setMyNotificationStatus] = useState<GetNotificationStatus>({
        emailNotificationStatus: 'N',
        emailsPopupHintData: {
            default: null,
            digest: null,
            notification: null,
            expired: null,
            immediate: null,
            immediate_sms: null,
        },
    });

    const showSwitch = controlProps?.showSwitch ?? false;

    const isReceivingPage =
        location.pathname == '/notification/receiving' || location.pathname == '/notification/receiving/';

    const gridOverrideCss = `
    .my-notification-off [data-test="grid_notificationSubscriptionGrid"] .Mui-checked+.MuiSwitch-track {
        background-color: ${alpha(appTheme.palette.text.primary, 0.64)};
        border-color: ${alpha(appTheme.palette.text.primary, 0.64)};
    }
    `;

    const onNotificationChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        myNotificationAPI.changeStatus(checked ? 'Y' : 'N').then((response) => {
            if (response.status === 'OK') {
                setInitialState(false);
                setNotificationStatus(checked);
            }
        });
    };

    useEffect(() => {
        if (!showSwitch) {
            return;
        }

        myNotificationAPI.getStatusInfo().then((response) => {
            setMyNotificationStatus(response.data);
            setNotificationStatus(response.data.emailNotificationStatus === 'Y');
        });

        return () => {
            document.body.classList.remove('my-notification-off');
        };
    }, [showSwitch]);

    useEffect(() => {
        if (notificationStatus) {
            document.body.classList.remove('my-notification-off');
        } else {
            document.body.classList.add('my-notification-off');
        }
    }, [notificationStatus]);

    const emailsPopupHint = () => {
        const items: string[] = [];
        const hintData = myNotificationStatus.emailsPopupHintData;

        if (hintData.digest != null) {
            items.push(t('init_new_msg_alert_digest_sent', { email: hintData.digest }));
        }

        if (hintData.immediate != null) {
            if (hintData.immediate_sms != null) {
                items.push(
                    t('init_new_msg_imdt_alert_sent_sms', { email: hintData.immediate, sms: hintData.immediate_sms }),
                );
            } else {
                items.push(t('init_new_msg_imdt_alert_sent', { email: hintData.immediate }));
            }
        } else {
            if (hintData.immediate_sms != null) {
                items.push(t('init_new_msg_imdt_alert_sms', { sms: hintData.immediate_sms }));
            }
        }

        if (hintData.expired != null) {
            items.push(t('init_new_msg_expire_sent', { email: hintData.expired }));
        }

        if (items.length === 0) {
            items.push(t('init_new_msg_all_email_sent', { email: hintData.notification }));
        } else {
            items.push(t('init_new_msg_all_other_email_sent', { email: hintData.notification }));
        }

        return (
            <ul>
                {items.map((item, index) => (
                    <li key={index}>{item}</li>
                ))}
            </ul>
        );
    };

    return (
        <Box
            sx={{
                backgroundColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.04),
                borderBottom: '1px solid',
                borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                width: '100%',
                height: '28px',
                '&:hover .anim-pulsing': {
                    animation: 'none',
                },
            }}
        >
            <Stack
                direction="row"
                alignItems={'center'}
                justifyContent="center"
                spacing={0.5}
                sx={{ height: '100%' }}
                className={
                    !notificationStatus && (!initialState || isReceivingPage) && showSwitch ? 'anim-pulsing' : ''
                }
            >
                {showSwitch && (
                    <>
                        <Switch
                            data-test={`my-notification-info-control`}
                            onChange={onNotificationChange}
                            checked={notificationStatus}
                            color={notificationStatus ? 'success' : 'error'}
                            sx={{
                                '.MuiSwitch-switchBase:not(.Mui-checked):not(.Mui-disabled) + .MuiSwitch-track': {
                                    backgroundColor: (theme) => theme.palette.error.main,
                                    borderColor: (theme) => theme.palette.error.main,
                                    opacity: 1,
                                },
                                '.MuiSwitch-switchBase:not(.Mui-checked):not(.Mui-disabled):hover + .MuiSwitch-track': {
                                    backgroundColor: (theme) => lighten(theme.palette.error.main, 0.24),
                                    borderColor: (theme) => lighten(theme.palette.error.main, 0.24),
                                },
                                '.MuiSwitch-switchBase:not(.Mui-checked):not(.Mui-disabled):hover > .MuiSwitch-thumb, .MuiSwitch-thumb':
                                    {
                                        backgroundColor: (theme) => theme.palette.background.default,
                                    },
                            }}
                        />
                        <Typography
                            sx={{
                                color: notificationStatus ? 'success.main' : 'error.main',
                            }}
                        >{`${t('notification')} ${notificationStatus ? t('on') : t('off')}`}</Typography>
                        <Tooltip title={emailsPopupHint()} placement={'bottom-start'}>
                            <span dangerouslySetInnerHTML={{ __html: t('for_emails') }} />
                        </Tooltip>
                    </>
                )}
                <SystemDateTimeLabel />
            </Stack>
            {isReceivingPage && <style>{gridOverrideCss}</style>}
        </Box>
    );
}

export class MyNotificationInfoBuilder extends FormComponentBuilder {
    prepareProps(): MyNotificationInfoBuilderProps {
        return {
            ...this.controlProps,
            showSwitch: this.elementProps.componentProps?.showSwitch ?? false,
        };
    }
}
